import React, { useState } from 'react';
import { Upload, X, AlertCircle, Check, Info } from 'lucide-react';
import { api } from '../utils/api';

export function ImageUploadModal({ isOpen, onClose, onUploadSuccess }) {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [error, setError] = useState(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const validateAndUpload = async (file) => {
    setError(null);
    
    // Client-side validation
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      setError('File too large. Maximum size: 50MB');
      return;
    }

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/tiff'];
    if (!validTypes.includes(file.type)) {
      setError('Invalid file type. Please upload JPEG, PNG, or TIFF');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.request('/book-covers/upload-image', {
        method: 'POST',
        body: formData,
        isFormData: true
      });

      if (response.success) {
        setUploadedImage(response.image);
        onUploadSuccess(response.image);
      }
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      await validateAndUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = async (e) => {
    if (e.target.files && e.target.files[0]) {
      await validateAndUpload(e.target.files[0]);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-2xl font-bold mb-2">Upload Your Own Image</h2>
              <p className="text-sm text-gray-600">Use your own artwork or photography for your book cover</p>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Upload Specs */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-2">
              <Info className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-blue-900 mb-2">Image Requirements:</p>
                <ul className="space-y-1 text-blue-800">
                  <li>• <strong>Format:</strong> JPEG, PNG, or TIFF</li>
                  <li>• <strong>Size:</strong> Maximum 50MB</li>
                  <li>• <strong>Dimensions:</strong> Minimum 1000x1000px</li>
                  <li>• <strong>Recommended:</strong> 2560x1600px (Kindle eBook) or 2550x3300px (Print 6x9")</li>
                  <li>• <strong>Resolution:</strong> 300 DPI for best quality</li>
                </ul>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {uploadedImage && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-green-900 mb-2">Upload Successful!</p>
                  <div className="text-sm text-green-800 space-y-1">
                    <p>• Filename: {uploadedImage.filename}</p>
                    <p>• Dimensions: {uploadedImage.width}x{uploadedImage.height}px</p>
                    <p>• Resolution: {uploadedImage.dpi} DPI</p>
                    <p>• Size: {uploadedImage.file_size_mb.toFixed(2)}MB</p>
                  </div>
                  <button
                    onClick={onClose}
                    className="mt-3 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                  >
                    Continue to Design →
                  </button>
                </div>
              </div>
            </div>
          )}

          {!uploadedImage && (
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-12 text-center transition ${
                dragActive
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-300 hover:border-purple-400'
              }`}
            >
              <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-semibold mb-2">Drag and drop your image here</p>
              <p className="text-sm text-gray-600 mb-4">or</p>
              <label className="inline-block">
                <input
                  type="file"
                  onChange={handleFileInput}
                  accept="image/jpeg,image/jpg,image/png,image/tiff"
                  className="hidden"
                  disabled={uploading}
                />
                <span className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 cursor-pointer inline-block">
                  {uploading ? 'Uploading...' : 'Browse Files'}
                </span>
              </label>
            </div>
          )}

          <div className="mt-6 flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
